import { parseMp } from '@church/nextjs-ui/website';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mar 4, 1988" from YYYY-MM-DD or an MP wall-clock string. Null-safe. */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = parseMp(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Mar 4" (year only when it is not this one). */
export function fmtShortDate(value: string, now: Date = new Date()): string {
  const d = parseMp(value);
  const base = `${MON[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

/** Today's date as YYYY-MM-DD in the browser's zone. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** YYYY-MM-DD strings compare as dates. */
export function isPast(iso: string | null): boolean {
  return !!iso && iso.slice(0, 10) < todayIso();
}

/** "$1,234.56", "$25" */
export function fmtMoney(n: number): string {
  const whole = Number.isInteger(n);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** "(248) 555-0134" as the user types. */
export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function phoneOk(raw: string): boolean {
  return raw.replace(/\D/g, '').length === 10;
}

export function emailOk(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
}

/** "Daughter · 12", "Spouse", "Head of Household" */
export function memberSubline(p: {
  relationship: { label: string } | null;
  age: number | null;
  position: string | null;
}): string {
  if (p.relationship)
    return [p.relationship.label, p.age !== null ? String(p.age) : null]
      .filter(Boolean)
      .join(' · ');
  return p.position ?? '';
}

export const KIND_LABEL: Record<string, string> = {
  allergy: 'Allergy',
  condition: 'Condition',
  medication: 'Medication',
  epipen: 'EpiPen',
  inhaler: 'Inhaler',
  device: 'Device',
};

/** "05 Lake Orion - General Fund" is a ledger code plus a name; people get the name. */
export function programLabel(name: string): string {
  return name.replace(/^\d{1,4}\s+[-–·]?\s*/, '').trim() || name;
}
